/**
 * StageProbabilitiesCard — Settings → Sales (Phase 2 / 2.2, P2-7): the
 * per-stage probability an UNASSESSED deal inherits. Four rows, one per open
 * stage; each row says whether the number was SET for this workspace or is
 * INHERITED from the platform default — the distinction the wire carries as
 * `source`, and the reason the ladder exists at all. "Use platform default"
 * resets a set stage (a null on the wire); the PUT carries only changed
 * stages (stageProbabilities.ts). Owner/admin write; any member can read.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { apiErrorMessage } from '../../../../api/client'
import { LoadingState, ErrorState } from '../../DashboardShared'
import { useAsync, fetchStageProbabilities, updateStageProbabilities } from '../../api'
import { useWriteAccess } from '../../../../lib/writeAccess'
import type { SalesStageProbability } from '../../../../api/types'
import {
  draftFromLadder, ladderDiff, ladderInputValid, ladderSavable, ladderSourceLabel, STAGE_LABEL,
  type LadderDraftRow,
} from './stageProbabilities'
import { accent } from '../../../../theme/tokens'

const inputCls = 'w-24 px-3 py-2 border border-edge-control rounded-control text-13 text-ink-primary text-right transition-all'

export function StageProbabilitiesCard() {
  const { data, loading, error } = useAsync(fetchStageProbabilities, [])
  // The table owns its draft; a saved ladder re-keys it so the draft
  // re-initialises from the server's answer (no state-in-effect).
  const [saved, setSaved] = useState<{ n: number; stages: SalesStageProbability[] } | null>(null)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const stages = saved?.stages ?? data!.stages
  return (
    <LadderTable key={saved?.n ?? 0} initial={stages}
      onSaved={next => setSaved(s => ({ n: (s?.n ?? 0) + 1, stages: next }))} />
  )
}

function LadderTable({ initial, onSaved }: { initial: SalesStageProbability[]; onSaved: (next: SalesStageProbability[]) => void }) {
  const [rows, setRows] = useState<LadderDraftRow[]>(() => draftFromLadder(initial))
  const [saving, setSaving] = useState(false)
  // The ladder is a records-level write (lib/writeAccess): without access the
  // table reads only — the stored figures, no inputs, no Save.
  const { records } = useWriteAccess()

  const setValue = (stage: string, value: string) =>
    setRows(rs => rs.map(r => (r.stage === stage ? { ...r, value } : r)))

  async function save() {
    const body = ladderDiff(rows)
    if (Object.keys(body).length === 0) return
    setSaving(true)
    try {
      const next = await updateStageProbabilities(body)
      toast.success('Stage probabilities saved')
      onSaved(next.stages)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to save stage probabilities'))
      setSaving(false)
    }
  }

  return (
    <div className="k-card">
      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-13 font-semibold text-ink-primary">Stage probabilities</div>
            <div className="text-12 text-ink-muted mt-1">
              The chance of winning a deal that nobody has assessed yet, by stage. A deal with its own probability keeps it; every other open deal inherits the number for its stage, and the weighted pipeline is computed from these.
            </div>
          </div>
          {records && (
            <button type="button" className="k-btn" disabled={!ladderSavable(rows, saving)} onClick={() => void save()}>
              {saving ? 'Saving…' : 'Save ladder'}
            </button>
          )}
        </div>
        <table className="sales-table w-full" aria-label="Stage probabilities">
          <thead>
            <tr>
              <th className="text-left">Stage</th>
              <th className="text-right">Probability</th>
              <th className="text-left">Source</th>
              <th className="text-right"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => <LadderRow key={r.stage} row={r} readOnly={!records} onChange={v => setValue(r.stage, v)} />)}
          </tbody>
        </table>
        <div className="text-11 text-ink-muted">
          Platform defaults: {rows.map(r => `${STAGE_LABEL[r.stage] ?? r.stage} ${r.stored.defaultProbability}%`).join(' · ')}.{records && ' Leave a stage blank and save to go back to its default.'}
        </div>
      </div>
    </div>
  )
}

function LadderRow({ row, readOnly, onChange }: { row: LadderDraftRow; readOnly: boolean; onChange: (v: string) => void }) {
  const stored: SalesStageProbability = row.stored
  const set = stored.source === 'tenant'
  const invalid = !ladderInputValid(row.value)
  const pendingReset = set && row.value.trim() === ''
  return (
    <tr>
      <td className="text-13 text-ink-primary font-medium">{STAGE_LABEL[row.stage] ?? row.stage}</td>
      <td className="text-right">
        {readOnly ? (
          <span className="text-13 text-ink-primary">{stored.probability}%</span>
        ) : (
        <span className="inline-flex items-center gap-1 justify-end">
          <input
            type="number" min={0} max={100} step={1} inputMode="numeric"
            value={row.value}
            placeholder={String(stored.defaultProbability)}
            onChange={e => onChange(e.target.value)}
            aria-label={`${STAGE_LABEL[row.stage] ?? row.stage} probability`}
            aria-invalid={invalid || undefined}
            className={inputCls + (invalid ? ' border-danger' : '')}
          />
          <span className="text-12 text-ink-muted">%</span>
        </span>
        )}
      </td>
      <td>
        {/* The P2-7 distinction, in a capsule and a sentence: SET (accent) vs
            INHERITED (muted). A set stage shows the platform default it
            overrides so a reader knows what "reset" would restore. */}
        <span className="inline-flex items-center gap-2">
          {set
            ? <span className="k-badge" style={{ background: accent.soft, color: accent.DEFAULT }}><span className="k-badge-dot" />SET</span>
            : <span className="k-badge-outline text-ink-muted"><span className="k-badge-dot" />INHERITED</span>}
          <span className="text-12 text-ink-muted">
            {pendingReset ? 'Will use the platform default on save' : ladderSourceLabel(stored)}
            {set && !pendingReset && ` (default ${stored.defaultProbability}%)`}
          </span>
        </span>
      </td>
      <td className="text-right">
        {!readOnly && set && !pendingReset && (
          <button type="button" className="k-btn-quiet" onClick={() => onChange('')}>Use platform default</button>
        )}
      </td>
    </tr>
  )
}
